import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';

import { NS } from '@/constants';
import { useScreenSizeContext } from '@/components/ScreenSize.context';

import { useAppDispatch } from '@/redux';
import { setBlogResults } from '@/redux/slices/blog.slice';

import { getBlogImageSrc } from '@/utils/blogUtils';
import { scrollToElement } from '@/utils/scroll';

import CustomHeading from '@/components/blog/CustomHeading';
import { Pagination } from '@/components/categ/Pagination';
import ArticleCardList from '@/components/blog/ArticleCardList';

import styles from './TagAndArchiveContent.module.scss';
import { ARTICLE_CARD_TYPE, POSITION_TYPE } from '@/constants/blog';

export const TagAndArchiveContent = ({ title, articlesList, paginationCateg, filterArticlesList,
  moreDetails = null, scrollTo }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation([NS.COMMON, NS.BLOG]);
  const nodeRef = useRef<HTMLDivElement>(null);
  const [firstImagesSrc] = useState(articlesList.content.slice(0, 3));
  const isMobile = useScreenSizeContext().isMobile;
  const customization = {
    customClassName: { articleCardList: styles['articles'] },
    headingLevel: { articleCard: 2 },
    position: { articleCard: POSITION_TYPE.START }
  };

  const filterByPage = async (page) => {
    filterArticlesList(page);
  };

  const updatePaginationMetaTag = (results) => {
    dispatch(setBlogResults({ blogResults: results }));
  };

  useEffect(() => {
    updatePaginationMetaTag(articlesList);
    if (scrollTo) {
      scrollToElement(nodeRef, { behavior: 'instant', block: 'start', inline: 'start' });
    }
  }, [articlesList, scrollTo]);

  return (
    <>
      {firstImagesSrc && (
        <Head>
          {firstImagesSrc.map((article) => (
            <link
              key={article.id}
              rel="preload"
              href={getBlogImageSrc(article.articles[0].imageName, isMobile, ARTICLE_CARD_TYPE.SMALL)} as="image"
            />
          ))}
        </Head>
      )}

      <div className={`${styles['container']} ${isMobile && styles.mobile}`} data-rbf="tag-and-archive-content">
        <CustomHeading
          title={title}
          headingLevel={1}
          labelPosition={POSITION_TYPE.CENTER}
          customClassName={{ label: styles['title'] }}
        />

        <div ref={nodeRef}>
          <ArticleCardList
            articles={articlesList.content}
            articleCardType="SMALL"
            customization={customization}
            tagOrArchive={true}
          />
        </div>

        {articlesList.totalPages > 1 ? (
          <div className={styles['pagination']}>
            <Pagination
              t={t}
              categ={paginationCateg}
              page={articlesList.pageable.pageNumber + 1}
              nbTotalRows={articlesList.totalElements}
              pageSize={articlesList.pageable.pageSize}
              onChange={filterByPage}
              isMobile={isMobile}
            />
          </div>
        ) : <></>}

        {moreDetails && (
          <div className={styles['more-details']} data-test-id="more-details-section">
            <p className={styles['details-title']}>
              {t('tagPage.moreDetails', { ns: NS.BLOG })}
            </p>
            <div className={styles['details-text']} dangerouslySetInnerHTML={{ __html: moreDetails }}></div>
          </div>
        )}
      </div>
    </>
  );
};

TagAndArchiveContent.propTypes = {
  title: PropTypes.string.isRequired,
  articlesList: PropTypes.object.isRequired,
  paginationCateg: PropTypes.object.isRequired,
  filterArticlesList: PropTypes.func.isRequired,
  moreDetails: PropTypes.string,
  scrollTo: PropTypes.bool
};

export default TagAndArchiveContent;
